import type { ReactNode } from 'react';
import * as Popover from '@radix-ui/react-popover';
import { useMedia } from '../hooks';

export type Show = 'all' | 'attention' | 'no_lead' | 'time_off' | 'unconfirmed' | 'clash';
export const SHOW_LABEL: Record<Show, string> = {
  all: 'Everything',
  attention: 'Needs attention',
  no_lead: 'No lead',
  time_off: 'Contractor on time off',
  unconfirmed: 'Not confirmed',
  clash: 'Clashes',
};
/** The narrower checks, kept behind the Filters button; "Needs attention" sits on the toolbar. */
const NARROW: Exclude<Show, 'all' | 'attention'>[] = [
  'no_lead',
  'time_off',
  'unconfirmed',
  'clash',
];

function FilterIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="1rem"
      height="1rem"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M4 6h16M7 12h10M10 18h4" />
    </svg>
  );
}

/**
 * The Schedule's less-used filters in one popover: which problem to look at,
 * and whose visits. Each choice shows how many visits it would find, so an
 * empty one is visible before it is picked.
 */
export function ScheduleFilters(props: {
  show: Show;
  counts: Record<Exclude<Show, 'all'>, number>;
  onShow(show: Show): void;
  /** Whose visits to show; none picked shows everyone's. */
  contractor: number[];
  contractors: Map<number, string>;
  /** How many visits each contractor has in view, under the other filters. */
  contractorCounts: Map<number, number>;
  onContractor(ids: number[]): void;
  client: number;
  clients: Map<number, string>;
  onClient(id: number): void;
  /** Cancelled and missed visits are hidden unless asked for. */
  cancelled: boolean;
  cancelledCount: number;
  onCancelled(on: boolean): void;
  /** Everything in here back to its default, in one step. */
  onClear(): void;
  /** Anything else worth keeping to hand, such as the colour key on a phone. */
  extra?: ReactNode;
}) {
  const active =
    (NARROW.includes(props.show as (typeof NARROW)[number]) ? 1 : 0) +
    (props.contractor.length ? 1 : 0) +
    (props.client ? 1 : 0) +
    (props.cancelled ? 1 : 0);
  // On a phone the popover stops above the bottom tab bar rather than running under it.
  const phone = useMedia('(max-width: 720px)');
  const byName = (m: Map<number, string>) => [...m].sort((a, b) => a[1].localeCompare(b[1]));

  return (
    <Popover.Root>
      <Popover.Trigger asChild>
        <button type="button" className={active ? 'chip filter-button on' : 'chip filter-button'}>
          <FilterIcon />
          Filters
          {active > 0 && (
            <span className="filter-count">
              {active}
              <span className="sr-only"> on</span>
            </span>
          )}
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          className="filter-pop"
          align="start"
          sideOffset={6}
          collisionPadding={{ top: 16, right: 16, left: 16, bottom: phone ? 84 : 16 }}
          aria-label="Schedule filters"
        >
          <fieldset className="filter-group">
            <legend>Show</legend>
            {(['any', ...NARROW] as const).map((id) => {
              // "Needs attention" is set from the toolbar; here it reads as "any visit" too.
              const on =
                id === 'any'
                  ? !NARROW.includes(props.show as (typeof NARROW)[number])
                  : props.show === id;
              return (
                <label key={id} className={on ? 'filter-option on' : 'filter-option'}>
                  <input
                    type="radio"
                    name="schedule-show"
                    checked={on}
                    onChange={() => props.onShow(id === 'any' ? 'all' : id)}
                  />
                  <span>{id === 'any' ? 'Any visit' : `Only ${SHOW_LABEL[id].toLowerCase()}`}</span>
                  {id !== 'any' && <span className="filter-option-count">{props.counts[id]}</span>}
                </label>
              );
            })}
            <label className={props.cancelled ? 'filter-option on' : 'filter-option'}>
              <input
                type="checkbox"
                checked={props.cancelled}
                onChange={(e) => props.onCancelled(e.target.checked)}
              />
              <span>Also cancelled and missed</span>
              <span className="filter-option-count">{props.cancelledCount}</span>
            </label>
          </fieldset>
          <fieldset className="filter-group filter-people">
            <legend>Contractors</legend>
            <label className={props.contractor.length ? 'filter-option' : 'filter-option on'}>
              <input
                type="checkbox"
                checked={props.contractor.length === 0}
                onChange={() => props.onContractor([])}
              />
              <span>Everyone</span>
            </label>
            {byName(props.contractors).map(([id, name]) => {
              const on = props.contractor.includes(id);
              return (
                <label key={id} className={on ? 'filter-option on' : 'filter-option'}>
                  <input
                    type="checkbox"
                    checked={on}
                    onChange={() =>
                      props.onContractor(
                        on ? props.contractor.filter((c) => c !== id) : [...props.contractor, id],
                      )
                    }
                  />
                  <span>{name}</span>
                  <span className="filter-option-count">{props.contractorCounts.get(id) ?? 0}</span>
                </label>
              );
            })}
          </fieldset>
          <label className="filter-field">
            Client
            <select value={props.client} onChange={(e) => props.onClient(Number(e.target.value))}>
              <option value={0}>All clients</option>
              {byName(props.clients).map(([id, name]) => (
                <option key={id} value={id}>
                  {name}
                </option>
              ))}
            </select>
          </label>
          {props.extra}
          <div className="filter-foot">
            <button type="button" className="link" disabled={active === 0} onClick={props.onClear}>
              Clear filters
            </button>
            <Popover.Close asChild>
              <button type="button">Done</button>
            </Popover.Close>
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}

/** What is filtering the view right now, each removable with one click. */
export function ActiveFilters(props: {
  items: { key: string; label: string; clear(): void }[];
  onClearAll(): void;
}) {
  if (props.items.length === 0) return null;
  return (
    <div className="active-filters" aria-label="Active filters" role="group">
      {props.items.map((f) => (
        <span key={f.key} className="active-filter">
          {f.label}
          <button type="button" aria-label={`Remove filter: ${f.label}`} onClick={f.clear}>
            <svg viewBox="0 0 16 16" width="12" height="12" aria-hidden="true" focusable="false">
              <path
                d="M4 4l8 8M12 4l-8 8"
                stroke="currentColor"
                strokeWidth="1.75"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </span>
      ))}
      {props.items.length > 1 && (
        <button type="button" className="link" onClick={props.onClearAll}>
          Clear all
        </button>
      )}
    </div>
  );
}
